import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card, CardContent, CardHeader, CardTitle,
  Button, Badge
} from '../../components/UI';
import apiClient from '../../utils/api';
import {
  Send, Bot, User, RefreshCw, ArrowLeft, Save, BarChart3,
  Plus, Trash2, DollarSign, CheckCircle, XCircle, Loader2
} from 'lucide-react';

const BudgetV2AIChat = ({ budgetId, budgetName, fiscalYear, subsidiaryId, subsidiaryName, onBack, onSave }) => {
  const navigate = useNavigate();
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Chat state
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Budget state built through chat
  const [categories, setCategories] = useState([]);
  const [categorizedAccounts, setCategorizedAccounts] = useState(null);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Expense accounts state
  const [expenseAccountsCount, setExpenseAccountsCount] = useState(0);

  // Initial AI greeting with predefined categories and expense accounts
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        // Fetch both categories and expense accounts in parallel
        const [categoriesRes, accountsRes] = await Promise.all([
          apiClient.get('/api/budget-v2/categories/predefined'),
          apiClient.get('/api/budget-v2/netsuite/expense-accounts')
        ]);

        const predefinedCategories = categoriesRes?.categories || [];
        const expenseAccounts = accountsRes?.accounts || [];
        setExpenseAccountsCount(expenseAccounts.length);

        const categoryList = predefinedCategories.map(c => `  - ${c.name} (${c.account_number})`).join('\n');

        const initialMessage = {
          role: 'assistant',
          content: `Hello! I'm here to help you create your budget "${budgetName}" for FY ${fiscalYear}.
${subsidiaryId ? `\n**Subsidiary:** ${subsidiaryName}` : ''}

**Predefined Budget Categories:**
${categoryList}

**Expense Accounts from NetSuite:** ${expenseAccounts.length} accounts found

You can tell me things like:
- "Use all predefined categories"
- "Add Marketing and Technology categories"
- "Add a custom category called R&D"
- "Categorize my expense accounts" (I'll assign ${expenseAccounts.length} accounts to categories)
- "Fetch expense accounts from NetSuite"
- "Get subsidiaries list"
- "Remove Travel category"
- "Save the budget"

What would you like to do?`,
          data: {
            type: 'initial_data',
            categories: predefinedCategories,
            expenseAccountsCount: expenseAccounts.length
          },
          timestamp: new Date()
        };
        setMessages([initialMessage]);
      } catch (error) {
        console.error('Error fetching initial data:', error);
        const fallbackMessage = {
          role: 'assistant',
          content: `Hello! I'm here to help you create your budget "${budgetName}" for FY ${fiscalYear}.

What categories would you like to add? You can say things like:
- "Use predefined categories"
- "Add Marketing, Technology, and Travel categories"

What would you like to do?`,
          timestamp: new Date()
        };
        setMessages([fallbackMessage]);
      }
    };

    fetchInitialData();
  }, [budgetName, fiscalYear, subsidiaryId, subsidiaryName]);

  const addMessage = (role, content, data = null) => {
    setMessages(prev => [...prev, {
      role,
      content,
      data,
      timestamp: new Date()
    }]);
  };

  const handleSend = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage = inputValue.trim();
    setInputValue('');
    addMessage('user', userMessage);
    setIsLoading(true);

    try {
      // Send to backend AI chat endpoint
      const response = await apiClient.post('/api/budget-v2/ai/chat', {
        budget_id: budgetId,
        message: userMessage,
        current_categories: categories,
        context: {
          budget_name: budgetName,
          fiscal_year: fiscalYear,
          subsidiary_id: subsidiaryId,
          subsidiary_name: subsidiaryName
        }
      });

      if (response.success) {
        // Add AI response
        addMessage('assistant', response.message, response.data);

        // Update state based on AI action
        if (response.action === 'add_categories' && response.categories) {
          setCategories(prev => [...prev, ...response.categories]);
        } else if (response.action === 'remove_category' && response.category_id) {
          setCategories(prev => prev.filter(c => c.id !== response.category_id));
        } else if (response.action === 'set_categories' && response.categories) {
          setCategories(response.categories);
        } else if (response.action === 'categorize_accounts' && response.results) {
          setCategorizedAccounts(response.results);
        } else if (response.action === 'save_budget') {
          // Save and navigate
          await saveBudget();
        }
      } else {
        addMessage('assistant', response.message || 'Sorry, I encountered an error. Please try again.');
      }
    } catch (error) {
      console.error('Chat error:', error);
      addMessage('assistant', 'Sorry, something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const saveBudget = async () => {
    if (categories.length === 0) {
      addMessage('assistant', 'You need to add at least one category before saving. Try saying "Add Marketing category" or "Show predefined categories".');
      return;
    }

    try {
      setIsLoading(true);

      // Prepare categories with accounts
      const categoriesToSave = categories.map(cat => {
        const catAccounts = categorizedAccounts?.categorized?.find(c => c.category_id === cat.id);
        return {
          name: cat.name,
          description: cat.description || '',
          budget_amount: cat.budget_amount || 0,
          expenses: (catAccounts?.accounts || []).map(acc => ({
            account_id: acc.id,
            account_number: acc.acctnumber,
            account_name: acc.acctname,
            amount: acc.suggested_amount || 0
          }))
        };
      });

      const response = await apiClient.post(`/api/budget-v2/budgets/${budgetId}/categories/bulk`, categoriesToSave);

      if (response.success) {
        addMessage('assistant', `Budget saved successfully with ${categories.length} categories! Redirecting to budget details...`);
        setTimeout(() => {
          navigate(`/budget-agent-v2/${budgetId}`);
        }, 1500);
      } else {
        addMessage('assistant', 'Failed to save budget. Please try again.');
      }
    } catch (error) {
      console.error('Save error:', error);
      addMessage('assistant', 'Error saving budget. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return '$0';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const renderMessageContent = (message) => {
    // Render special data if present
    if (message.data?.type === 'categories_list') {
      return (
        <div>
          <p className="mb-3">{message.content}</p>
          <div className="space-y-2">
            {message.data.categories.map((cat, idx) => (
              <div key={idx} className="flex items-center justify-between p-2 bg-white/50 dark:bg-gray-700/50 rounded">
                <span className="font-medium">{cat.name}</span>
                {cat.account_number && (
                  <Badge className="bg-gray-200 text-gray-700 text-xs">{cat.account_number}</Badge>
                )}
              </div>
            ))}
          </div>
        </div>
      );
    }

    if (message.data?.type === 'categorization_results') {
      const results = message.data.results;
      return (
        <div>
          <p className="mb-3">{message.content}</p>
          <div className="space-y-3">
            {results.categorized?.map((cat, idx) => (
              <div key={idx} className="p-3 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-semibold text-emerald-700 dark:text-emerald-300">{cat.category_name}</span>
                  <Badge className="bg-emerald-200 text-emerald-800">{cat.account_count} accounts</Badge>
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400 max-h-24 overflow-y-auto">
                  {cat.accounts.slice(0, 5).map((acc, i) => (
                    <div key={i}>{acc.acctnumber}: {acc.acctname}</div>
                  ))}
                  {cat.accounts.length > 5 && (
                    <div className="text-gray-400">...and {cat.accounts.length - 5} more</div>
                  )}
                </div>
              </div>
            ))}
            {results.uncategorized?.length > 0 && (
              <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-amber-700 dark:text-amber-300">Uncategorized</span>
                  <Badge className="bg-amber-200 text-amber-800">{results.uncategorized.length} accounts</Badge>
                </div>
              </div>
            )}
          </div>
        </div>
      );
    }

    // Regular text message
    return <p className="whitespace-pre-wrap">{message.content}</p>;
  };

  return (
    <div className="flex flex-col h-[calc(100vh-200px)] max-w-4xl mx-auto">
      {/* Header */}
      <Card className="mb-4">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="sm" onClick={onBack}>
                <ArrowLeft className="w-4 h-4" />
              </Button>
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                  <Bot className="w-5 h-5 text-purple-500" />
                  AI Budget Assistant
                </h2>
                <p className="text-sm text-gray-500">
                  {budgetName} | FY {fiscalYear}{subsidiaryId ? ` | ${subsidiaryName}` : ''}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {categories.length > 0 && (
                <Badge className="bg-emerald-100 text-emerald-700">
                  {categories.length} Categories
                </Badge>
              )}
              <Button
                onClick={saveBudget}
                disabled={isLoading || categories.length === 0}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                <Save className="w-4 h-4 mr-2" />
                Save Budget
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Current Categories Summary */}
      {categories.length > 0 && (
        <Card className="mb-4">
          <CardContent className="p-3">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm text-gray-500">Categories:</span>
              {categories.map((cat, idx) => (
                <Badge key={idx} className="bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300">
                  {cat.name}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Chat Messages */}
      <Card className="flex-1 overflow-hidden">
        <CardContent className="p-4 h-full flex flex-col">
          <div className="flex-1 overflow-y-auto space-y-4 mb-4">
            {messages.map((message, idx) => (
              <div
                key={idx}
                className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div className={`flex items-start gap-2 max-w-[80%] ${message.role === 'user' ? 'flex-row-reverse' : ''}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                    message.role === 'user'
                      ? 'bg-blue-500'
                      : 'bg-purple-500'
                  }`}>
                    {message.role === 'user' ? (
                      <User className="w-4 h-4 text-white" />
                    ) : (
                      <Bot className="w-4 h-4 text-white" />
                    )}
                  </div>
                  <div className={`rounded-lg p-3 ${
                    message.role === 'user'
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white'
                  }`}>
                    {renderMessageContent(message)}
                  </div>
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="flex justify-start">
                <div className="flex items-center gap-2 bg-gray-100 dark:bg-gray-700 rounded-lg p-3">
                  <Loader2 className="w-4 h-4 animate-spin text-purple-500" />
                  <span className="text-gray-500">Thinking...</span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="flex items-center gap-2 border-t pt-4">
            <input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Tell me what you want to do..."
              disabled={isLoading}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white disabled:opacity-50"
            />
            <Button
              onClick={handleSend}
              disabled={isLoading || !inputValue.trim()}
              className="bg-purple-600 hover:bg-purple-700"
            >
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default BudgetV2AIChat;
